/* Content Library console — pure model (lane bc-content-studio-ux; design
 * reports-findings/bc-content-studio-ux-2026-10-24/ux-audit-design-2026-10-24_1831.md §4).
 * DOM-free and dependency-free so it runs under `node --test --experimental-strip-types`.
 *
 * Owns: URL <-> filter state, date presets (local calendar, Monday weeks), the
 * API query the server validates, saved-view labels, DD-MM-YYYY display from ISO
 * strings ONLY (never `new Date(<string>)` — cross-browser rule 24-10-2026),
 * WordPress/channel labels and bulk-action eligibility. The server owns every
 * filter, count and the tenancy scope; nothing here widens what the API returns. */

export type LibraryView = 'needs_review' | 'ready_to_publish' | 'scheduled' | 'published_month' | 'failed' | 'empty' | 'all';
export type LibraryType = 'all' | 'blog' | 'social';
export type DatePreset = 'any' | 'today' | 'this_week' | 'last_7' | 'this_month' | 'last_month' | 'month' | 'custom';
export type DateField = 'updated' | 'created' | 'scheduled' | 'published';
export type LibrarySort = 'updated' | 'created' | 'title';
export type LibraryScope = 'domain' | 'unassigned' | 'all';
export type LibraryLayout = 'auto' | 'table' | 'cards';

export interface LibraryFilters {
  view: LibraryView | null; // null = not chosen yet (default: Needs review when it has items, else All)
  type: LibraryType;
  q: string;
  date: DatePreset;
  month: string; // YYYY-MM (date === 'month')
  from: string; // YYYY-MM-DD (date === 'custom')
  to: string;
  dateField: DateField;
  status: string[];
  wp: string[];
  channel: string[];
  source: string[];
  target: string;
  keyword: string;
  sort: LibrarySort;
  scope: LibraryScope;
  layout: LibraryLayout;
}

export const DEFAULT_FILTERS: LibraryFilters = {
  view: null, type: 'all', q: '', date: 'any', month: '', from: '', to: '', dateField: 'updated',
  status: [], wp: [], channel: [], source: [], target: '', keyword: '', sort: 'updated', scope: 'domain', layout: 'auto',
};

export const VIEWS: Array<{ key: LibraryView; label: string; tone?: 'danger' }> = [
  { key: 'needs_review', label: 'Needs review' },
  { key: 'ready_to_publish', label: 'Ready to publish' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'published_month', label: 'Published this month' },
  { key: 'failed', label: 'Failed', tone: 'danger' },
  { key: 'empty', label: 'Empty drafts' },
  { key: 'all', label: 'All' },
];

export const DATE_PRESETS: Array<{ key: DatePreset; label: string }> = [
  { key: 'any', label: 'Any time' },
  { key: 'today', label: 'Today' },
  { key: 'this_week', label: 'This week' },
  { key: 'last_7', label: 'Last 7 days' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'month', label: 'Pick a month…' },
  { key: 'custom', label: 'Custom range…' },
];

export const DATE_FIELDS: Array<{ key: DateField; label: string }> = [
  { key: 'updated', label: 'Last updated' },
  { key: 'created', label: 'Created' },
  { key: 'scheduled', label: 'Scheduled for' },
  { key: 'published', label: 'Published' },
];

export const STATUS_OPTIONS: Array<{ key: string; label: string }> = [
  { key: 'ready', label: 'Blog: ready' },
  { key: 'needs_update', label: 'Blog: needs update' },
  { key: 'generating', label: 'Blog: generating' },
  { key: 'draft', label: 'Social: draft' },
  { key: 'in_review', label: 'Social: awaiting approval' },
  { key: 'approved', label: 'Social: approved' },
  { key: 'rejected', label: 'Social: changes requested' },
];

export const WP_OPTIONS: Array<{ key: string; label: string }> = [
  { key: 'none', label: 'Not pushed' },
  { key: 'draft', label: 'WordPress draft' },
  { key: 'scheduled', label: 'Scheduled in WordPress' },
  { key: 'published', label: 'Published on WordPress' },
  { key: 'failed', label: 'Push failed' },
];

export const SOURCE_OPTIONS: Array<{ key: string; label: string }> = [
  { key: 'aura_auto', label: 'Aura auto (SEO autopilot)' },
  { key: 'aura', label: 'Aura' },
  { key: 'manual', label: 'Manual' },
];

export const SORT_OPTIONS: Array<{ key: LibrarySort; label: string }> = [
  { key: 'updated', label: 'Last updated' },
  { key: 'created', label: 'Newest created' },
  { key: 'title', label: 'Title A–Z' },
];

const CHANNEL_SHORT: Record<string, string> = {
  facebook: 'FB', instagram: 'IG', linkedin: 'LI', x: 'X', tiktok: 'TT', pinterest: 'PIN',
  youtube: 'YT', threads: 'TH', google_business: 'GBP', generic: 'Post',
};
const CHANNEL_LONG: Record<string, string> = {
  facebook: 'Facebook', instagram: 'Instagram', linkedin: 'LinkedIn', x: 'X', tiktok: 'TikTok', pinterest: 'Pinterest',
  youtube: 'YouTube', threads: 'Threads', google_business: 'Google Business', generic: 'Post',
};
export const channelShort = (c: string): string => CHANNEL_SHORT[c] ?? c.toUpperCase().slice(0, 3);
export const channelName = (c: string): string => CHANNEL_LONG[c] ?? c;

const VIEW_SET = new Set<string>(VIEWS.map((v) => v.key));
const pick = <T extends string>(v: string | null, allowed: readonly T[], fallback: T): T =>
  v !== null && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
const csv = (v: string | null): string[] => (v ? v.split(',').map((s) => s.trim()).filter(Boolean) : []);
const isMonth = (v: string): boolean => /^\d{4}-(0[1-9]|1[0-2])$/.test(v);
export const isIsoDay = (v: string): boolean => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return false;
  const y = Number(m[1]); const mo = Number(m[2]); const d = Number(m[3]);
  return mo >= 1 && mo <= 12 && d >= 1 && d <= daysInMonth(y, mo);
};

/** Parse URL search params into filters. Unknown values fall back to defaults. */
export function filtersFromParams(sp: URLSearchParams): LibraryFilters {
  const f: LibraryFilters = { ...DEFAULT_FILTERS };
  const v = sp.get('v');
  f.view = v !== null && VIEW_SET.has(v) ? (v as LibraryView) : null;
  f.type = pick(sp.get('type'), ['all', 'blog', 'social'] as const, 'all');
  f.q = (sp.get('q') ?? '').slice(0, 120);
  f.date = pick(sp.get('date'), DATE_PRESETS.map((d) => d.key), 'any');
  f.month = isMonth(sp.get('month') ?? '') ? (sp.get('month') as string) : '';
  f.from = isIsoDay(sp.get('from') ?? '') ? (sp.get('from') as string) : '';
  f.to = isIsoDay(sp.get('to') ?? '') ? (sp.get('to') as string) : '';
  if (f.date === 'month' && f.month === '') f.date = 'any';
  if (f.date === 'custom' && f.from === '' && f.to === '') f.date = 'any';
  f.dateField = pick(sp.get('df'), DATE_FIELDS.map((d) => d.key), 'updated');
  f.status = csv(sp.get('status')).filter((s) => STATUS_OPTIONS.some((o) => o.key === s));
  f.wp = csv(sp.get('wp')).filter((s) => WP_OPTIONS.some((o) => o.key === s));
  f.channel = csv(sp.get('ch')).filter((s) => s in CHANNEL_SHORT);
  f.source = csv(sp.get('src')).filter((s) => SOURCE_OPTIONS.some((o) => o.key === s));
  f.target = (sp.get('target') ?? '').slice(0, 400);
  f.keyword = (sp.get('kw') ?? '').slice(0, 200);
  f.sort = pick(sp.get('sort'), ['updated', 'created', 'title'] as const, 'updated');
  f.scope = pick(sp.get('scope'), ['domain', 'unassigned', 'all'] as const, 'domain');
  f.layout = pick(sp.get('layout'), ['auto', 'table', 'cards'] as const, 'auto');
  return f;
}

/** Serialise filters to URL params, omitting defaults so URLs stay short + shareable. */
export function filtersToParams(f: LibraryFilters): URLSearchParams {
  const sp = new URLSearchParams();
  const set = (k: string, v: string, def = '') => { if (v !== def && v !== '') sp.set(k, v); };
  if (f.view !== null) sp.set('v', f.view);
  set('type', f.type, 'all');
  set('q', f.q.trim());
  set('date', f.date, 'any');
  if (f.date === 'month') set('month', f.month);
  if (f.date === 'custom') { set('from', f.from); set('to', f.to); }
  set('df', f.dateField, 'updated');
  set('status', f.status.join(','));
  set('wp', f.wp.join(','));
  set('ch', f.channel.join(','));
  set('src', f.source.join(','));
  set('target', f.target);
  set('kw', f.keyword);
  set('sort', f.sort, 'updated');
  set('scope', f.scope, 'domain');
  set('layout', f.layout, 'auto');
  return sp;
}

// ---- dates (local calendar; numbers only, never string parsing) ---------------

export interface DayParts { y: number; m: number; d: number } // m = 1..12
const pad = (n: number): string => (n < 10 ? '0' + n : String(n));
export const iso = (p: DayParts): string => `${p.y}-${pad(p.m)}-${pad(p.d)}`;
export function daysInMonth(y: number, m: number): number {
  return [31, (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0 ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1];
}
export function addDays(p: DayParts, n: number): DayParts {
  let { y, m, d } = p;
  d += n;
  while (d < 1) { m -= 1; if (m < 1) { m = 12; y -= 1; } d += daysInMonth(y, m); }
  while (d > daysInMonth(y, m)) { d -= daysInMonth(y, m); m += 1; if (m > 12) { m = 1; y += 1; } }
  return { y, m, d };
}
/** Day of week, Monday = 0 … Sunday = 6 (Zeller-free Sakamoto). */
export function weekdayMon0(p: DayParts): number {
  const t = [0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4];
  const y = p.m < 3 ? p.y - 1 : p.y;
  const sun0 = (y + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) + t[p.m - 1] + p.d) % 7;
  return (sun0 + 6) % 7;
}
export function todayParts(now: Date = new Date()): DayParts {
  return { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate() };
}

/** Resolve a date preset to an inclusive [from, to] ISO-day range (null = no bound). */
export function dateRange(f: Pick<LibraryFilters, 'date' | 'month' | 'from' | 'to'>, today: DayParts): { from: string | null; to: string | null } {
  switch (f.date) {
    case 'today': return { from: iso(today), to: iso(today) };
    case 'this_week': return { from: iso(addDays(today, -weekdayMon0(today))), to: iso(today) };
    case 'last_7': return { from: iso(addDays(today, -6)), to: iso(today) };
    case 'this_month': return { from: iso({ ...today, d: 1 }), to: iso({ ...today, d: daysInMonth(today.y, today.m) }) };
    case 'last_month': {
      const y = today.m === 1 ? today.y - 1 : today.y; const m = today.m === 1 ? 12 : today.m - 1;
      return { from: iso({ y, m, d: 1 }), to: iso({ y, m, d: daysInMonth(y, m) }) };
    }
    case 'month': {
      if (!isMonth(f.month)) return { from: null, to: null };
      const y = Number(f.month.slice(0, 4)); const m = Number(f.month.slice(5, 7));
      return { from: iso({ y, m, d: 1 }), to: iso({ y, m, d: daysInMonth(y, m) }) };
    }
    case 'custom': {
      const from = isIsoDay(f.from) ? f.from : null; const to = isIsoDay(f.to) ? f.to : null;
      if (from && to && from > to) return { from: to, to: from };
      return { from, to };
    }
    default: return { from: null, to: null };
  }
}

/** The last N months (newest first) as {value: YYYY-MM, label: 'October 2026'}. */
export function recentMonths(today: DayParts, n = 18): Array<{ value: string; label: string }> {
  const names = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const out: Array<{ value: string; label: string }> = [];
  let y = today.y; let m = today.m;
  for (let i = 0; i < n; i++) {
    out.push({ value: `${y}-${pad(m)}`, label: `${names[m - 1]} ${y}` });
    m -= 1; if (m < 1) { m = 12; y -= 1; }
  }
  return out;
}

/** 'YYYY-MM-DD[ T]HH:MM:SS' -> 'DD-MM-YYYY'. Anything else -> '' (never guessed). */
export function formatDay(value: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
}
/** 'YYYY-MM-DD HH:MM:SS' -> 'DD-MM-YYYY HH:MM' (UTC as stored). */
export function formatDayTime(value: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(value ?? '');
  return m ? `${m[3]}-${m[2]}-${m[1]} ${m[4]}:${m[5]}` : formatDay(value);
}

// ---- API query -------------------------------------------------------------------

/** The GET /v1/content/library query. The scope is the shell's Viewing domain by
 *  default; 'unassigned' / 'all' are explicit lenses. A domain scope with no
 *  selected domain sends no project_id, and the server fails closed (empty). */
export function apiQuery(f: LibraryFilters, effectiveView: LibraryView, projectId: number | null, today: DayParts, cursor?: string | null, limit = 50): Record<string, string> {
  const q: Record<string, string> = { view: effectiveView, limit: String(limit) };
  if (f.scope === 'all') q.project_id = 'all';
  else if (f.scope === 'unassigned') q.project_id = 'unassigned';
  else if (projectId != null && projectId > 0) q.project_id = String(projectId);
  if (f.type !== 'all') q.type = f.type;
  if (f.q.trim() !== '') q.q = f.q.trim();
  const r = dateRange(f, today);
  if (r.from) q.from = r.from;
  if (r.to) q.to = r.to;
  if (r.from || r.to) q.date_field = f.dateField;
  if (f.status.length) q.status = f.status.join(',');
  if (f.wp.length) q.wp = f.wp.join(',');
  if (f.channel.length) q.channel = f.channel.join(',');
  if (f.source.length) q.source = f.source.join(',');
  if (f.target) q.target_page = f.target;
  if (f.keyword) q.keyword = f.keyword;
  if (f.sort !== 'updated') q.sort = f.sort;
  if (cursor) q.cursor = cursor;
  return q;
}

/** Number of non-default refinements (drives the mobile "Filters (n)" badge). */
export function activeFilterCount(f: LibraryFilters): number {
  let n = 0;
  if (f.type !== 'all') n++;
  if (f.date !== 'any') n++;
  if (f.status.length) n++;
  if (f.wp.length) n++;
  if (f.channel.length) n++;
  if (f.source.length) n++;
  if (f.target) n++;
  if (f.keyword) n++;
  if (f.scope !== 'domain') n++;
  return n;
}

/** Default view rule (design §4.3): Needs review when it has items, else All. */
export function effectiveView(f: LibraryFilters, needsReviewCount: number | null): LibraryView {
  if (f.view !== null) return f.view;
  if (needsReviewCount === null) return 'needs_review';
  return needsReviewCount > 0 ? 'needs_review' : 'all';
}

// ---- labels ---------------------------------------------------------------------

export type Tone = 'neutral' | 'ai' | 'success' | 'warning' | 'danger';

export function wpLabel(state: string | null | undefined): { label: string; tone: Tone } {
  switch (state) {
    case 'draft': return { label: 'WP draft', tone: 'ai' };
    case 'scheduled': return { label: 'Scheduled in WP', tone: 'warning' };
    case 'published': return { label: 'Published', tone: 'success' };
    case 'failed': return { label: 'Push failed', tone: 'danger' };
    default: return { label: 'Not pushed', tone: 'neutral' };
  }
}

export function blogStatusLabel(status: string): { label: string; tone: Tone } {
  switch (status) {
    case 'ready': return { label: 'Ready', tone: 'success' };
    case 'needs_update': return { label: 'Needs update', tone: 'warning' };
    case 'generating': return { label: 'Generating', tone: 'warning' };
    case 'failed': return { label: 'Failed', tone: 'danger' };
    default: return { label: status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Draft', tone: 'neutral' };
  }
}

export function sourceLabel(source: string): string {
  return source === 'aura_auto' ? 'Aura auto' : source === 'aura' ? 'Aura' : 'Manual';
}

/** Short label for a target page: the path, or the host for a root URL. */
export function targetLabel(url: string | null | undefined): string {
  if (!url) return '';
  const m = /^(?:https?:\/\/)?(?:www\.)?([^/?#]+)([^?#]*)/i.exec(url.trim());
  if (!m) return url;
  const path = m[2].replace(/\/+$/, '');
  return path === '' ? m[1].toLowerCase() : path;
}

// ---- bulk actions (only what the per-item flows already allow; D5) ---------------

export interface BulkItem { kind: 'blog' | 'social'; id: number; status: string }
export type BulkAction = 'approve' | 'request_changes' | 'submit_review' | 'delete';

/** Which selected items a bulk action applies to. Approve / request changes:
 *  social posts awaiting approval only. Submit for review: social drafts and
 *  changes-requested posts. Delete (soft): any item. There is no bulk publish. */
export function bulkTargets(items: BulkItem[], action: BulkAction): BulkItem[] {
  switch (action) {
    case 'approve':
    case 'request_changes': return items.filter((i) => i.kind === 'social' && i.status === 'in_review');
    case 'submit_review': return items.filter((i) => i.kind === 'social' && (i.status === 'draft' || i.status === 'rejected'));
    case 'delete': return items.slice();
    default: return [];
  }
}

export const itemKey = (i: { kind: string; id: number }): string => `${i.kind}:${i.id}`;
